/**
 * Etappe 1 der Bestandsübernahme: nachsehen, was in der Ablage liegt.
 *
 * Der Kunde hat nie Stammdaten gepflegt. Kundennummern, Anschriften und
 * Spielplatznamen stecken in alten Prüfberichten, verteilt über einen
 * gewachsenen Ordnerbaum. Bevor daraus etwas gelesen wird, muss klar sein,
 * womit man es zu tun hat.
 *
 * Diese Seite legt nichts an. Sie zählt und berichtet — und sagt das auch,
 * damit niemand nach dem Lauf Kunden in der Liste sucht.
 *
 * Die eine Zahl, auf die es ankommt, steht deshalb hervorgehoben: ob die
 * PDFs eine Textebene haben. Davon hängt ab, ob Etappe 2 ein Auslesen ist
 * oder eine Texterkennung — ein Unterschied in Aufwand und
 * Verlässlichkeit.
 */

import { useState } from 'react'
import {
  type Inventurlauf,
  useInventurStarten,
  useInventurlaeufe,
  useInventurlauf,
} from '../api/inventur'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'
import { Altbestand } from './Altbestand'

function mb(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${(bytes / 1024).toFixed(0)} KB`
}

function zeit(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Inventur() {
  const laeufe = useInventurlaeufe()
  const starten = useInventurStarten()
  const [ordner, setzeOrdner] = useState('')
  const [offen, setzeOffen] = useState<string | null>(null)

  // Solange etwas läuft, wird der Stand abgefragt; danach nicht mehr.
  const laufend = (laeufe.data ?? []).find((l) => l.status === 'laeuft')
  const beobachtet = useInventurlauf(offen ?? laufend?.id ?? null)

  if (laeufe.isLoading) return <Ladeanzeige />

  const gezeigt = beobachtet.data ?? null

  return (
    <div className="space-y-4">
      <div className="karte space-y-3">
        <h2 className="text-xl font-bold">Ablage ansehen</h2>
        <p className="text-base text-slate-700">
          Läuft den Ordnerbaum der Dokumentenablage durch und zählt, was
          darin liegt: Dateien je Endung, Ordnertiefe, Gesamtgröße, und bei
          PDFs, ob sie durchsuchbar sind oder Scans.
        </p>
        <p className="rounded-feld border-2 border-slate-200 bg-slate-50 p-2 text-base">
          <span className="font-semibold">Es wird nur gelesen.</span> Es
          entstehen keine Kunden, keine Spielplätze und keine Dateien — dieser
          Schritt sagt nur, was da ist.
        </p>

        <label htmlFor="inv-ordner">
          Ordner in der Bibliothek (leer = ganze Bibliothek)
        </label>
        <input
          id="inv-ordner"
          value={ordner}
          placeholder="z. B. Kunden/Archiv"
          onChange={(e) => setzeOrdner(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="knopf-haupt"
            disabled={starten.isPending || laufend !== undefined}
            onClick={() =>
              starten.mutate(ordner, { onSuccess: (l) => setzeOffen(l.id) })
            }
          >
            {starten.isPending ? 'Startet …' : 'Inventur starten'}
          </button>
          {laufend && (
            <span className="text-base text-slate-700">
              Es läuft bereits eine Inventur.
            </span>
          )}
        </div>
        <Fehlerhinweis fehler={starten.error} />
      </div>

      {gezeigt && <Lauf lauf={gezeigt} />}

      {/* Etappe 2 hängt an einem fertigen Lauf: erst sehen, was da ist,
          dann daraus Kunden und Plätze vorschlagen. */}
      {gezeigt?.status === 'fertig' && <Altbestand laufId={gezeigt.id} />}

      <div className="karte space-y-2">
        <h2 className="text-xl font-bold">Frühere Läufe</h2>
        {(laeufe.data ?? []).length === 0 ? (
          <Leerhinweis titel="Noch keine Inventur gelaufen" />
        ) : (
          <ul className="space-y-1">
            {(laeufe.data ?? []).map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className="karte-knopf"
                  onClick={() => setzeOffen(l.id)}
                >
                  <span className="flex-1">
                    <span className="block font-semibold">
                      {l.ordner || '(ganze Bibliothek)'}
                    </span>
                    <span className="block text-base text-slate-700">
                      {zeit(l.begonnen_am)}
                      {l.benutzer_name ? ` · ${l.benutzer_name}` : ''} ·{' '}
                      {l.dateien} Dateien
                    </span>
                  </span>
                  <span className="marke bg-marke-100 text-marke-700">
                    {l.status === 'laeuft'
                      ? 'läuft'
                      : l.status === 'fertig'
                        ? 'fertig'
                        : 'gescheitert'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function Lauf({ lauf }: { lauf: Inventurlauf }) {
  if (lauf.status === 'gescheitert') {
    return (
      <div className="karte space-y-2">
        <h2 className="text-xl font-bold text-stufe-3">Inventur gescheitert</h2>
        <p className="text-base">{lauf.fehler}</p>
        <p className="text-base text-slate-600">
          Bei „403“ fehlt die Freigabe der Website für die Anwendung, bei
          „404“ stimmt der Ordner nicht.
        </p>
      </div>
    )
  }

  if (lauf.status === 'laeuft') {
    return (
      <div className="karte space-y-2">
        <h2 className="text-xl font-bold">Läuft …</h2>
        {/* Kein Balken: die Gesamtzahl ist erst am Ende bekannt, und ein
            Balken, der eine unbekannte Länge vortäuscht, ist eine falsche
            Auskunft. Die Zahlen genügen. */}
        <p aria-live="polite" className="text-lg tabular-nums">
          {lauf.dateien} Dateien in {lauf.ordner_anzahl} Ordnern ·{' '}
          {mb(lauf.bytes_gesamt)}
        </p>
        <p className="text-base text-slate-600">
          Der Lauf geht weiter, auch wenn Sie diese Seite verlassen.
        </p>
      </div>
    )
  }

  const e = lauf.ergebnis
  if (!e) return null
  const pdf = e.pdf

  return (
    <div className="karte space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-bold">Ergebnis</h2>
        <span className="text-base text-slate-700">
          {lauf.ordner || '(ganze Bibliothek)'} · {zeit(lauf.begonnen_am)}
        </span>
        <a
          className="knopf-zweit ml-auto px-3 text-base"
          href={`/api/inventur/${lauf.id}/bericht.csv`}
        >
          Als CSV
        </a>
      </div>

      <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[14rem_1fr]">
        <dt className="text-slate-600">Dateien</dt>
        <dd className="font-bold tabular-nums">{e.dateien}</dd>
        <dt className="text-slate-600">Ordner</dt>
        <dd className="font-bold tabular-nums">{e.ordner}</dd>
        <dt className="text-slate-600">Gesamtgröße</dt>
        <dd className="font-bold tabular-nums">{mb(e.bytes)}</dd>
        <dt className="text-slate-600">Größte Ordnertiefe</dt>
        <dd className="font-bold tabular-nums">{e.max_tiefe}</dd>
      </dl>

      <div>
        <h3 className="mb-1 font-semibold">Dateien je Endung</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="text-left text-slate-600">
                <th className="py-1 pr-4">Endung</th>
                <th className="py-1 pr-4 text-right">Anzahl</th>
                <th className="py-1 pr-4 text-right">Größe</th>
                <th className="py-1">Beispiel</th>
              </tr>
            </thead>
            <tbody>
              {e.endungen.map((z) => (
                <tr key={z.endung} className="border-t border-slate-200 align-top">
                  <td className="py-1 pr-4 font-semibold">.{z.endung}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{z.anzahl}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{mb(z.bytes)}</td>
                  <td className="py-1 break-all text-slate-600">
                    {z.beispiele[0] ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pdf.gesamt > 0 && (
        <div className="rounded-feld border-2 border-marke-600 p-3">
          <h3 className="font-semibold">
            PDFs: durchsuchbar oder Scan — Stichprobe
          </h3>
          <p className="text-base text-slate-700">
            Von <span className="tabular-nums">{pdf.gesamt}</span> PDFs wurden{' '}
            <span className="font-bold tabular-nums">{pdf.geprueft}</span>{' '}
            geöffnet. Nicht alle: das hieße, die halbe Ablage
            herunterzuladen, um eine Frage zu beantworten, die eine Handvoll
            Dateien ebenso beantwortet.
          </p>
          <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-[14rem_1fr]">
            <dt className="text-slate-600">mit Textebene</dt>
            <dd className="font-bold tabular-nums">{pdf.mit_text}</dd>
            <dt className="text-slate-600">Scan ohne Text</dt>
            <dd className="font-bold tabular-nums">{pdf.scan}</dd>
            <dt className="text-slate-600">nicht lesbar</dt>
            <dd className="font-bold tabular-nums">{pdf.unlesbar}</dd>
          </dl>
          {pdf.geprueft > 0 && (
            <p className="mt-2 text-base">
              {pdf.scan > pdf.mit_text
                ? 'Überwiegend Scans. Aus ihnen lässt sich ohne Texterkennung nichts auslesen.'
                : 'Überwiegend durchsuchbar. Der Text lässt sich direkt auslesen.'}
            </p>
          )}
        </div>
      )}

      {e.tiefste_pfade.length > 0 && (
        <div>
          <h3 className="mb-1 font-semibold">Tiefste Pfade</h3>
          <ul className="space-y-1 text-base text-slate-700">
            {e.tiefste_pfade.map((p) => (
              <li key={p} className="break-all">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
